import type { ErrorInfo, RefObject } from "react";
import * as Sentry from "@sentry/react";
import { useTranslate } from "@refinedev/core";
import {
  PdfDocumentViewer,
  type PdfViewerHandle,
  type TextSelectionHandler,
} from "@libertify/experience-viewer";
import { AlertCard, DocumentPaper } from "@libertify/ui";
import { KEY } from "./constants";
import type { StandoutHighlight } from "./types";

export type StandoutsDocumentPaneProps = {
  highlight: StandoutHighlight;
  viewerRef: RefObject<PdfViewerHandle>;
  projectId: string;
  /** Called with each text selected in the document. */
  onTextSelection: TextSelectionHandler;
};

/**
 * The viewer's own error boundary catches its crashes before any page-level
 * Sentry boundary does, so report them here.
 */
const reportViewerError = (error: Error, errorInfo: ErrorInfo) => {
  Sentry.captureException(error, {
    contexts: { react: { componentStack: errorInfo.componentStack } },
  });
};

/**
 * The project's document, with the standout mark drawn over it. The viewer
 * shows its own loading state, and keeps showing it while a load fails.
 */
export const StandoutsDocumentPane = ({
  highlight,
  viewerRef,
  projectId,
  onTextSelection,
}: StandoutsDocumentPaneProps) => {
  const t = useTranslate();
  const { activeDocument } = highlight;

  if (!activeDocument) {
    return (
      <AlertCard
        type="warning"
        title={t(`${KEY}.noDocumentTitle`)}
        description={t(`${KEY}.noDocumentBody`)}
      />
    );
  }

  return (
    <>
      <div style={{ flex: 1, minHeight: 0 }}>
        <DocumentPaper>
          <PdfDocumentViewer
            viewerRef={viewerRef}
            onError={reportViewerError}
            url={activeDocument.url}
            highlights={highlight.highlights}
            cuepoints={highlight.cuepoints}
            documentPagesCount={activeDocument.pageCount}
            identifiers={{ projectId }}
            onTextSelection={onTextSelection}
          />
        </DocumentPaper>
      </div>
    </>
  );
};

export default StandoutsDocumentPane;
